/**
 * PaymentActionSheet — modale unique pour valider ou rappeler un paiement.
 * ─────────────────────────────────────────────────────────────────────────
 * Simplification radicale du PaymentPanel : au lieu d'un gros bloc permanent,
 * la modale ne s'ouvre que lorsqu'il y a réellement une action à faire,
 * déclenchée par la NextActionCard.
 *
 * Deux modes :
 *   • "confirm" — valider la réception du paiement (montant + méthode)
 *   • "remind"  — envoyer un rappel via le moteur de communication
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, CheckCircle2, Send, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/wyvio-ui";
import api from "@/lib/api";

const METHODS = [
  { value: "wire",      label: "Virement bancaire" },
  { value: "card",      label: "Carte bancaire" },
  { value: "cash",      label: "Espèces" },
  { value: "check",     label: "Chèque" },
  { value: "financing", label: "Financement" },
  { value: "other",     label: "Autre" },
];

const Shell = ({ title, onClose, testId, children }) => {
  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  return createPortal(
    <div
      data-testid={testId}
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
    >
      <div
        onClick={onClose}
        className="absolute inset-0 bg-[#111111]/25 backdrop-blur-[2px]"
      />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-[440px] bg-white dark:bg-[#111111] rounded-t-3xl sm:rounded-3xl border border-[#EEF2F5] dark:border-[#262626] p-6 max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <h3 className="text-[19px] font-semibold tracking-[-0.02em] text-[#0A0A0A] dark:text-white">
            {title}
          </h3>
          <button
            onClick={onClose}
            aria-label="Fermer"
            className="h-8 w-8 rounded-full flex items-center justify-center text-[#6B7280] dark:text-[#A3A3A3] hover:text-[#0A0A0A] dark:hover:text-white hover:bg-[#F7F8FA] dark:hover:bg-[#1A1A1A]"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
};

const PaymentConfirm = ({ dossierId, initial, onClose, onDone }) => {
  const [amount, setAmount] = useState(
    initial?.declared_amount ?? initial?.amount_due ?? "",
  );
  const [method, setMethod] = useState(
    initial?.declared_method || initial?.method || "wire",
  );
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!amount || busy) return;
    setBusy(true); setErr("");
    try {
      await api.confirmPayment(dossierId, {
        amount: parseFloat(amount),
        method,
        notes: notes || null,
      });
      onDone?.();
    } catch (e) {
      setErr(e.message || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Valider la réception du paiement" onClose={onClose} testId="payment-confirm-sheet">
      <p className="text-[13.5px] text-[#6B7280] dark:text-[#A3A3A3] leading-relaxed">
        Cette action marque le paiement comme reçu et débloque automatiquement
        la suite du parcours.
      </p>

      <div className="mt-5 space-y-4">
        <label className="block">
          <span className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-[#6B7280] dark:text-[#A3A3A3]">
            Montant reçu
          </span>
          <div className="mt-2 flex gap-2">
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="pcs-amount"
              autoFocus
              className="flex-1 h-11 rounded-xl border border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#0A0A0A] px-4 text-[14px] outline-none focus:border-[#0A0A0A] dark:focus:border-white text-[#0A0A0A] dark:text-white"
              placeholder="0.00"
            />
            <div className="h-11 px-4 rounded-xl bg-[#F7F8FA] dark:bg-[#1A1A1A] text-[13px] text-[#6B7280] dark:text-[#A3A3A3] flex items-center">
              {initial?.currency || "EUR"}
            </div>
          </div>
        </label>

        <label className="block">
          <span className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-[#6B7280] dark:text-[#A3A3A3]">
            Méthode
          </span>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            data-testid="pcs-method"
            className="mt-2 w-full h-11 rounded-xl border border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#0A0A0A] px-4 text-[14px] outline-none focus:border-[#0A0A0A] dark:focus:border-white text-[#0A0A0A] dark:text-white"
          >
            {METHODS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-[#6B7280] dark:text-[#A3A3A3]">
            Notes internes (facultatif)
          </span>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="pcs-notes"
            className="mt-2 w-full rounded-xl border border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#0A0A0A] px-4 py-3 text-[14px] outline-none focus:border-[#0A0A0A] dark:focus:border-white text-[#0A0A0A] dark:text-white resize-none"
            placeholder="Ex : virement reçu ce matin, ref BQ-9832"
          />
        </label>
      </div>

      {err && (
        <div className="mt-4 rounded-xl bg-[#FEE2E2] dark:bg-[#3B0F0F] text-[#991B1B] dark:text-[#FCA5A5] text-[12.5px] px-3 py-2 flex items-center gap-2">
          <AlertCircle size={14} />
          {err}
        </div>
      )}

      <Button
        variant="primary"
        size="lg"
        onClick={submit}
        disabled={!amount || busy}
        loading={busy}
        icon={CheckCircle2}
        data-testid="pcs-submit"
        className="mt-6 w-full justify-center h-11"
      >
        Valider le paiement
      </Button>
    </Shell>
  );
};

const PaymentRemind = ({ dossierId, onClose, onDone }) => {
  const [email, setEmail] = useState(true);
  const [notif, setNotif] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    const channels = [email && "email", notif && "notification"].filter(Boolean);
    if (channels.length === 0 || busy) return;
    setBusy(true); setErr("");
    try {
      await api.remindPayment(dossierId, { channels, force: true });
      onDone?.();
    } catch (e) {
      setErr(e.message || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Rappeler le paiement" onClose={onClose} testId="payment-remind-sheet">
      <p className="text-[13.5px] text-[#6B7280] dark:text-[#A3A3A3] leading-relaxed">
        Un rappel automatique sera envoyé au client via le moteur de communication.
      </p>

      <div className="mt-5 space-y-2.5">
        <label className="flex items-center gap-3 rounded-2xl border border-[#EEF2F5] dark:border-[#262626] px-4 py-3 cursor-pointer">
          <input
            type="checkbox"
            checked={email}
            onChange={(e) => setEmail(e.target.checked)}
            data-testid="prs-email"
            className="accent-[#0A0A0A]"
          />
          <span className="text-[14px] font-medium text-[#0A0A0A] dark:text-white">Email</span>
        </label>
        <label className="flex items-center gap-3 rounded-2xl border border-[#EEF2F5] dark:border-[#262626] px-4 py-3 cursor-pointer">
          <input
            type="checkbox"
            checked={notif}
            onChange={(e) => setNotif(e.target.checked)}
            data-testid="prs-notif"
            className="accent-[#0A0A0A]"
          />
          <span className="text-[14px] font-medium text-[#0A0A0A] dark:text-white">Notification interne</span>
        </label>
      </div>

      {err && (
        <div className="mt-4 rounded-xl bg-[#FEE2E2] dark:bg-[#3B0F0F] text-[#991B1B] dark:text-[#FCA5A5] text-[12.5px] px-3 py-2 flex items-center gap-2">
          <AlertCircle size={14} />
          {err}
        </div>
      )}

      <Button
        variant="primary"
        size="lg"
        onClick={submit}
        disabled={busy}
        loading={busy}
        icon={Send}
        data-testid="prs-submit"
        className="mt-6 w-full justify-center h-11"
      >
        Envoyer le rappel
      </Button>
    </Shell>
  );
};

const PaymentActionSheet = ({ mode, dossierId, initial, onClose, onDone }) => {
  if (mode === "confirm") {
    return <PaymentConfirm dossierId={dossierId} initial={initial} onClose={onClose} onDone={onDone} />;
  }
  if (mode === "remind") {
    return <PaymentRemind dossierId={dossierId} onClose={onClose} onDone={onDone} />;
  }
  return null;
};

export default PaymentActionSheet;
